// src/helpers/rutaClientes.js
import axios from "axios";

const RAW_BASE_URL =
  process.env.REACT_APP_API_BASE || "https://backre-miami.us-3.evennode.com";

const BASE_URL = String(RAW_BASE_URL).replace(/\/+$/, "");

const API = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

// normaliza rutas: "clientes" => "/clientes"
const p = (path) => `/${String(path || "").replace(/^\/+/, "")}`;

const getToken = () => localStorage.getItem("token") || "";

const auth = () => {
  const token = getToken();
  return token ? { headers: { token } } : {};
};

const debug =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase() === "true";

const logUrl = (url, params, data, method = "GET") => {
  if (!debug) return;

  console.log("👉 METHOD:", method);
  console.log("👉 URL:", API.getUri({ url, params }));

  if (data) {
    console.log("📦 DATA ENVIADA:", data);
  }
};

async function req(method, url, { params, data, signal } = {}) {
  logUrl(url, params, data, method);

  try {
    const res = await API.request({
      method,
      url,
      params,
      data,
      signal,
      ...auth(),
    });

    return res.data;
  } catch (err) {
    if (debug) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;

      console.log("🧯 API ERROR:", msg);
      console.log("🧯 STATUS:", err?.response?.status);
      console.log("🧯 DATA:", err?.response?.data);
    }

    throw err;
  }
}

/* ----------------------------- NORMALIZADORES ---------------------------- */

const clean = (v) => String(v ?? "").trim();

const unwrapCliente = (data) => {
  if (!data) return null;

  if (data?.cliente) return data.cliente;
  if (data?.data) return data.data;

  return data;
};

const unwrapClientes = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.clientes)) return data.clientes;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.docs)) return data.docs;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;

  if (Array.isArray(data?.clientes?.data)) return data.clientes.data;

  return [];
};

export const normalizarClientePayload = (payload = {}) => {
  const src = payload || {};

  return {
    ...src,

    // Datos personales
    apellido1: clean(src.apellido1),
    apellido2: clean(src.apellido2),
    nombre: clean(src.nombre),
    razonsocial: clean(src.razonsocial),

    // En pantalla dice ID, en backend usamos dni
    dni: clean(src.dni ?? src.idDocumento ?? src.documento),

    // Contacto
    telefono1: clean(
      src.telefono1 ?? src.telefono ?? src.mobile ?? src.celular1
    ),
    email: clean(src.email),

    // Dirección USA
    domicilio: clean(src.domicilio ?? src.addressLine1),
    direccion2: clean(src.direccion2 ?? src.addressLine2),
    ciudad: clean(src.ciudad ?? src.city),
    estado: clean(src.estado ?? src.state).toUpperCase(),
    zipCode: clean(src.zipCode ?? src.zip ?? src.zipcode ?? src.codpostal),
    pais: clean(src.pais ?? src.country) || "USA",

    // Fecha ingreso
    fecing: src.fecing || null,

    // Tipo de cliente dentro de Riders Miami
    // IMPORTANTE: respetamos false, por eso usamos ?? y no ||
    esComprador: src.esComprador ?? true,
    esGarante: src.esGarante ?? false,

    activo: src.activo ?? true,
  };
};

/* ------------------------------- CLIENTES ------------------------------- */

// GET /clientes?page=1&limit=50&q=...
// GET /clientes?page=1&limit=50&q=...&tipo=garante|comprador
export async function getClientes({
  page = 1,
  limit = 50,
  q = "",
  tipo = "",
  signal,
} = {}) {
  const params = {
    page,
    limit,
    q: String(q || "").trim(),
  };

  if (tipo) {
    params.tipo = String(tipo).trim();
  }

  const data = await req("GET", p("clientes"), {
    params,
    signal,
  });

  const clientes = unwrapClientes(data);

  return {
    clientes,
    total: Number(data?.total ?? clientes.length ?? 0),
    page: Number(data?.page ?? page),
    limit: Number(data?.limit ?? limit),
  };
}

export async function getClienteById(id, { signal } = {}) {
  const data = await req("GET", p(`clientes/${id}`), { signal });
  return unwrapCliente(data);
}

export async function postCliente(payload, { signal } = {}) {
  const data = normalizarClientePayload(payload);

  if (debug) {
    console.log("🟢 postCliente PAYLOAD NORMALIZADO:", data);
  }

  return req("POST", p("clientes"), {
    data,
    signal,
  });
}

export async function postGarante(payload, { signal } = {}) {
  return postCliente(
    {
      ...payload,
      esComprador: false,
      esGarante: true,
      activo: payload?.activo ?? true,
    },
    { signal }
  );
}

export async function putCliente(id, payload, { signal } = {}) {
  const data = normalizarClientePayload(payload);

  if (debug) {
    console.log("🟣 putCliente ID:", id);
    console.log("🟣 putCliente PAYLOAD ORIGINAL:", payload);
    console.log("🟢 putCliente PAYLOAD NORMALIZADO:", data);
    console.log("🌐 putCliente URL:", `${BASE_URL}${p(`clientes/${id}`)}`);
  }

  return req("PUT", p(`clientes/${id}`), {
    data,
    signal,
  });
}

export async function deleteCliente(id, { signal } = {}) {
  return req("DELETE", p(`clientes/${id}`), { signal });
}

export async function getClienteByCodigo(codcli, { signal } = {}) {
  const codigo = String(codcli || "").trim();

  if (!codigo) return null;

  const data = await req("GET", p(`clientes/codigo/${codigo}`), { signal });

  return unwrapCliente(data);
}

// Compatible con:
// buscarClientes({ q: "Elias", limit: 20 })
// buscarClientes("Elias", 20)
// Compatible con:
// buscarClientes({ q: "Elias", limit: 20 })
// buscarClientes({ q: "Elias", limit: 20, tipo: "garante" })
// buscarClientes("Elias", 20)
export async function buscarClientes(arg1 = {}, arg2 = 20) {
  let q = "";
  let limit = 20;
  let tipo = "";
  let signal;

  if (typeof arg1 === "string" || typeof arg1 === "number") {
    q = String(arg1 || "").trim();
    limit = Number(arg2 || 20);
  } else {
    q = String(arg1?.q || "").trim();
    limit = Number(arg1?.limit || 20);
    tipo = String(arg1?.tipo || "").trim();
    signal = arg1?.signal;
  }

  if (!q || q.length < 2) return [];

  const params = {
    q,
    limit,
  };

  if (tipo) {
    params.tipo = tipo;
  }

  const data = await req("GET", p("clientes/buscar"), {
    params,
    signal,
  });

  const clientes = unwrapClientes(data);

  if (debug) {
    console.log("🔎 buscarClientes q:", q);
    console.log("🔎 tipo:", tipo);
    console.log("🔎 clientes encontrados:", clientes);
  }

  return clientes;
}

export async function getGarantes({
  page = 1,
  limit = 50,
  q = "",
  signal,
} = {}) {
  return getClientes({
    page,
    limit,
    q,
    tipo: "garante",
    signal,
  });
}

export async function buscarGarantes({ q = "", limit = 20, signal } = {}) {
  return buscarClientes({
    q,
    limit,
    tipo: "garante",
    signal,
  });
}

/* -------------------------- DOCUMENTOS CLIENTE -------------------------- */

export async function subirDocumentoCliente(id, tipo, file, { signal } = {}) {
  if (!id) throw new Error("Falta ID del cliente.");
  if (!tipo) throw new Error("Falta tipo de documento.");
  if (!file) throw new Error("Falta archivo.");

  const formData = new FormData();
  formData.append("archivo", file);

  return req("POST", p(`clientes/${id}/documentos/${tipo}`), {
    data: formData,
    signal,
  });
}

export async function eliminarDocumentoCliente(id, tipo, { signal } = {}) {
  if (!id) throw new Error("Falta ID del cliente.");
  if (!tipo) throw new Error("Falta tipo de documento.");

  return req("DELETE", p(`clientes/${id}/documentos/${tipo}`), {
    signal,
  });
}